import { useState, useMemo } from 'react';
import PageWrapper from '../PageWrapper/PageWrapper';
import { AdminRestricted } from '../../components/AdminRestricted/AdminRestricted';
import { CardV2, CardHeader, CardTitle, CardContent } from '../../components/CardV2/CardV2';
import { TabV2, TabsContent, TabsList, TabsTrigger } from '../../components/TabV2/TabV2';
import { 
  ShoppingBag, 
  TrendingUp, 
  Bookmark, 
  Eye, 
  Globe, 
  DollarSign,
  BarChart3
} from 'lucide-react';
import { useStatistics } from '../../hooks/useStatistics';
import BarChart from '../../components/BarChart/BarChart';
import LinearGraph from '../../components/LinearGraph/LinearGraph';
import NoData from '../../components/NoData/NoData';
import CONFIG from '../../config/config';

type TimeRange = {
  start: string; // ISO date string
  end: string;   // ISO date string
};

const toISODate = (d: Date) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString();
};

function EcommerceStatisticsPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'items' | 'traffic'>('overview');

  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthEnd = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [timeRange, setTimeRange] = useState<TimeRange>({ 
    start: toISODate(monthStart), 
    end: toISODate(monthEnd) 
  });

  const onDateChange = (key: 'start' | 'end', value: string) => {
    const date = new Date(value);
    setTimeRange((prev) => ({ ...prev, [key]: toISODate(date) }));
  };

  const startInputValue = useMemo(() => new Date(timeRange.start).toISOString().slice(0, 10), [timeRange.start]);
  const endInputValue = useMemo(() => new Date(timeRange.end).toISOString().slice(0, 10), [timeRange.end]);

  const filters = useMemo(() => ({
    startTime: timeRange.start,
    endTime: timeRange.end,
  }), [timeRange.start, timeRange.end]);

  return (
    <PageWrapper>
      <AdminRestricted>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              <h1 className="text-2xl font-bold text-foreground">
                Ecommerce Insights
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">From</span>
                <input 
                  type="date" 
                  value={startInputValue} 
                  onChange={(e) => onDateChange('start', e.target.value)} 
                  className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" 
                />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">To</span>
                <input 
                  type="date" 
                  value={endInputValue} 
                  onChange={(e) => onDateChange('end', e.target.value)} 
                  className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" 
                />
              </div>
            </div>
          </div>

          <TabV2 value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="items" className="flex items-center gap-2">
                <ShoppingBag className="h-4 w-4" />
                Items
              </TabsTrigger>
              <TabsTrigger value="traffic" className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Traffic
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <TopSellingItems filters={filters} />
                <IncomeOverTime filters={filters} />
              </div>
            </TabsContent>

            <TabsContent value="items" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <TopSellingItems filters={filters} />
                <TopBookmarkedItems filters={filters} />
                <TopViewedItems filters={filters} />
              </div>
            </TabsContent>

            <TabsContent value="traffic" className="space-y-6">
              <VisitorSources filters={filters} />
            </TabsContent>
          </TabV2>
        </div>
      </AdminRestricted>
    </PageWrapper>
  );
}

// Top Selling Items Component
const TopSellingItems = ({ filters }: { filters: { startTime: string; endTime: string } }) => {
  const data = useStatistics('store-top-selling-items', filters);
  
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    // Handle structure: { [itemName]: salesCount } or { [period]: { [itemName]: count } }
    let items: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      // Check if it's nested structure (by period) or flat (by item)
      if (typeof firstValue === 'object' && firstValue !== null) {
        // Nested: { [period]: { [itemName]: count } }
        const allItems = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(item => allItems.add(item));
          }
        });
        
        items = Array.from(allItems).map(itemName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[itemName] || 0);
            }
          });
          return { name: itemName, count: total };
        });
      } else {
        // Flat: { [itemName]: count }
        items = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    items.sort((a, b) => b.count - a.count);
    const topItems = items.slice(0, 10);
    
    return [{
      label: 'Sales',
      data: topItems.map(item => item.count),
      backgroundColor: ['rgba(59, 130, 246, 0.6)'],
      borderColor: ['rgba(59, 130, 246, 0.8)'],
      borderWidth: 1,
    }];
  }, [data]);

  const tableData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    let items: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        const allItems = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(item => allItems.add(item));
          }
        });
        
        items = Array.from(allItems).map(itemName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[itemName] || 0);
            }
          });
          return { name: itemName, count: total };
        });
      } else {
        items = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    return items.sort((a, b) => b.count - a.count).slice(0, 20);
  }, [data]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Top Selling Items
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 && chartData[0].data.length > 0 ? (
          <div className="space-y-4">
            <BarChart 
              title="Top Selling Items" 
              xLabels={tableData.slice(0, 10).map(item => item.name)} 
              dataSets={chartData} 
            />
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium text-muted-foreground">Rank</th>
                    <th className="text-left p-2 font-medium text-muted-foreground">
                      Item Name
                    </th>
                    <th className="text-right p-2 font-medium text-muted-foreground">
                      Sales
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, index) => (
                    <tr key={item.name} className="border-b hover:bg-muted/50">
                      <td className="p-2 text-muted-foreground">#{index + 1}</td>
                      <td className="p-2 font-medium">{item.name}</td>
                      <td className="p-2 text-right font-semibold">{item.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <NoData message="No data available" />
        )}
      </CardContent>
    </CardV2>
  );
};

// Income Over Time Component
const IncomeOverTime = ({ filters }: { filters: { startTime: string; endTime: string } }) => {
  const data = useStatistics('store-income-over-time', filters);
  
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    // Handle structure: { [date]: income } or { [period]: { [currency]: amount } }
    let periods: string[] = [];
    let values: number[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        // Nested: { [period]: { [currency]: amount } }
        periods = Object.keys(data).sort();
        values = periods.map(period => {
          const periodData = data[period];
          if (typeof periodData === 'object') {
            return Object.values(periodData).reduce((sum: number, val: any) => {
              return sum + (typeof val === 'number' ? val : 0);
            }, 0);
          }
          return 0;
        });
      } else {
        // Flat: { [period]: income }
        periods = Object.keys(data).sort();
        values = periods.map(period => {
          const val = data[period];
          return typeof val === 'number' ? val : 0;
        });
      }
    }
    
    return [{
      label: 'Income',
      data: values,
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
      borderColor: 'rgba(16, 185, 129, 1)',
      borderWidth: 2,
    }];
  }, [data]);

  const totalIncome = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return 0;
    
    let total = 0;
    Object.values(data).forEach((value: any) => {
      if (typeof value === 'number') {
        total += value;
      } else if (typeof value === 'object' && value !== null) {
        Object.values(value).forEach((val: any) => {
          total += typeof val === 'number' ? val : 0;
        });
      }
    });
    
    return total;
  }, [data]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Income Over Time
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="text-3xl font-bold text-foreground">
            {CONFIG.SUPPORTED_CURRENCIES_SYMBOLS['EUR' as keyof typeof CONFIG.SUPPORTED_CURRENCIES_SYMBOLS] || '€'}
            {totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Total Income
          </p>
        </div>
        {chartData.length > 0 && chartData[0].data.length > 0 ? (
          <LinearGraph 
            title="Income Over Time" 
            xLabels={Object.keys(data).sort()} 
            dataSets={chartData} 
          />
        ) : (
          <NoData message="No data available" />
        )}
      </CardContent>
    </CardV2>
  );
};

// Top Bookmarked Items Component
const TopBookmarkedItems = ({ filters }: { filters: { startTime: string; endTime: string } }) => {
  const data = useStatistics('store-top-bookmarked-items', filters);
  
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    let items: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        const allItems = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(item => allItems.add(item));
          }
        });
        
        items = Array.from(allItems).map(itemName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[itemName] || 0);
            }
          });
          return { name: itemName, count: total };
        });
      } else {
        items = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    items.sort((a, b) => b.count - a.count);
    const topItems = items.slice(0, 10);
    
    return [{
      label: 'Bookmarks',
      data: topItems.map(item => item.count),
      backgroundColor: ['rgba(234, 179, 8, 0.6)'],
      borderColor: ['rgba(234, 179, 8, 0.8)'],
      borderWidth: 1,
    }];
  }, [data]);

  const tableData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    let items: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        const allItems = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(item => allItems.add(item));
          }
        });
        
        items = Array.from(allItems).map(itemName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[itemName] || 0);
            }
          });
          return { name: itemName, count: total };
        });
      } else {
        items = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    return items.sort((a, b) => b.count - a.count).slice(0, 20);
  }, [data]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bookmark className="h-5 w-5" />
          Top Bookmarked Items
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 && chartData[0].data.length > 0 ? (
          <div className="space-y-4">
            <BarChart 
              title="Top Bookmarked Items" 
              xLabels={tableData.slice(0, 10).map(item => item.name)} 
              dataSets={chartData} 
            />
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium text-muted-foreground">Rank</th>
                    <th className="text-left p-2 font-medium text-muted-foreground">
                      Item Name
                    </th>
                    <th className="text-right p-2 font-medium text-muted-foreground">
                      Bookmarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, index) => (
                    <tr key={item.name} className="border-b hover:bg-muted/50">
                      <td className="p-2 text-muted-foreground">#{index + 1}</td>
                      <td className="p-2 font-medium">{item.name}</td>
                      <td className="p-2 text-right font-semibold">{item.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <NoData message="No data available" />
        )}
      </CardContent>
    </CardV2>
  );
};

// Top Viewed Items Component
const TopViewedItems = ({ filters }: { filters: { startTime: string; endTime: string } }) => {
  const data = useStatistics('store-top-viewed-items', filters);
  
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    let items: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        const allItems = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(item => allItems.add(item));
          }
        });
        
        items = Array.from(allItems).map(itemName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[itemName] || 0);
            }
          });
          return { name: itemName, count: total };
        });
      } else {
        items = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    items.sort((a, b) => b.count - a.count);
    const topItems = items.slice(0, 10);
    
    return [{
      label: 'Views',
      data: topItems.map(item => item.count),
      backgroundColor: ['rgba(139, 92, 246, 0.6)'],
      borderColor: ['rgba(139, 92, 246, 0.8)'],
      borderWidth: 1,
    }];
  }, [data]);

  const tableData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    let items: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        const allItems = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(item => allItems.add(item));
          }
        });
        
        items = Array.from(allItems).map(itemName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[itemName] || 0);
            }
          });
          return { name: itemName, count: total };
        });
      } else {
        items = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    return items.sort((a, b) => b.count - a.count).slice(0, 20);
  }, [data]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5" />
          Top Viewed Items
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 && chartData[0].data.length > 0 ? (
          <div className="space-y-4">
            <BarChart 
              title="Top Viewed Items" 
              xLabels={tableData.slice(0, 10).map(item => item.name)} 
              dataSets={chartData} 
            />
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium text-muted-foreground">Rank</th>
                    <th className="text-left p-2 font-medium text-muted-foreground">
                      Item Name
                    </th>
                    <th className="text-right p-2 font-medium text-muted-foreground">
                      Views
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, index) => (
                    <tr key={item.name} className="border-b hover:bg-muted/50">
                      <td className="p-2 text-muted-foreground">#{index + 1}</td>
                      <td className="p-2 font-medium">{item.name}</td>
                      <td className="p-2 text-right font-semibold">{item.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <NoData message="No data available" />
        )}
      </CardContent>
    </CardV2>
  );
};

// Visitor Sources Component
const VisitorSources = ({ filters }: { filters: { startTime: string; endTime: string } }) => {
  const data = useStatistics('store-visitor-sources', filters);
  
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    // Handle structure: { [source]: count } or { [period]: { [source]: count } }
    let sources: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        // Nested: { [period]: { [source]: count } }
        const allSources = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(source => allSources.add(source));
          }
        });
        
        sources = Array.from(allSources).map(sourceName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[sourceName] || 0);
            }
          });
          return { name: sourceName, count: total };
        });
      } else {
        // Flat: { [source]: count }
        sources = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    sources.sort((a, b) => b.count - a.count);
    
    // Group similar sources (e.g., Instagram variations)
    const groupedSources: { [key: string]: number } = {};
    sources.forEach(source => {
      const normalizedName = source.name.toLowerCase();
      let key = source.name;
      
      if (normalizedName.includes('instagram') || normalizedName.includes('ig')) {
        key = 'Instagram';
      } else if (normalizedName.includes('facebook') || normalizedName.includes('fb')) {
        key = 'Facebook';
      } else if (normalizedName.includes('google')) {
        key = 'Google';
      } else if (normalizedName.includes('direct') || normalizedName.includes('none')) {
        key = 'Direct';
      } else if (normalizedName.includes('tiktok') || normalizedName.includes('tt')) {
        key = 'TikTok';
      } else if (normalizedName.includes('pinterest')) {
        key = 'Pinterest';
      }
      
      groupedSources[key] = (groupedSources[key] || 0) + source.count;
    });
    
    const groupedArray = Object.entries(groupedSources)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    
    return [{
      label: 'Visitors',
      data: groupedArray.map(item => item.count),
      backgroundColor: [
        'rgba(59, 130, 246, 0.6)',
        'rgba(16, 185, 129, 0.6)',
        'rgba(234, 179, 8, 0.6)',
        'rgba(239, 68, 68, 0.6)',
        'rgba(139, 92, 246, 0.6)',
        'rgba(236, 72, 153, 0.6)',
      ],
      borderColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(234, 179, 8, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(236, 72, 153, 0.8)',
      ],
      borderWidth: 1,
    }];
  }, [data]);

  const tableData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    let sources: { name: string; count: number }[] = [];
    
    if (Object.keys(data).length > 0) {
      const firstKey = Object.keys(data)[0];
      const firstValue = data[firstKey];
      
      if (typeof firstValue === 'object' && firstValue !== null) {
        const allSources = new Set<string>();
        Object.values(data).forEach((periodData: any) => {
          if (typeof periodData === 'object') {
            Object.keys(periodData).forEach(source => allSources.add(source));
          }
        });
        
        sources = Array.from(allSources).map(sourceName => {
          let total = 0;
          Object.values(data).forEach((periodData: any) => {
            if (typeof periodData === 'object') {
              total += (periodData[sourceName] || 0);
            }
          });
          return { name: sourceName, count: total };
        });
      } else {
        sources = Object.entries(data).map(([name, count]) => ({
          name,
          count: typeof count === 'number' ? count : 0
        }));
      }
    }
    
    // Group similar sources
    const groupedSources: { [key: string]: number } = {};
    sources.forEach(source => {
      const normalizedName = source.name.toLowerCase();
      let key = source.name;
      
      if (normalizedName.includes('instagram') || normalizedName.includes('ig')) {
        key = 'Instagram';
      } else if (normalizedName.includes('facebook') || normalizedName.includes('fb')) {
        key = 'Facebook';
      } else if (normalizedName.includes('google')) {
        key = 'Google';
      } else if (normalizedName.includes('direct') || normalizedName.includes('none')) {
        key = 'Direct';
      } else if (normalizedName.includes('tiktok') || normalizedName.includes('tt')) {
        key = 'TikTok';
      } else if (normalizedName.includes('pinterest')) {
        key = 'Pinterest';
      }
      
      groupedSources[key] = (groupedSources[key] || 0) + source.count;
    });
    
    return Object.entries(groupedSources)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const totalVisitors = useMemo(() => {
    return tableData.reduce((sum, item) => sum + item.count, 0);
  }, [tableData]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Visitor Sources
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="text-3xl font-bold text-foreground">
            {totalVisitors.toLocaleString()}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Total Visitors
          </p>
        </div>
        {chartData.length > 0 && chartData[0].data.length > 0 ? (
          <div className="space-y-4">
            <BarChart 
              title="Visitor Sources" 
              xLabels={tableData.map(item => item.name)} 
              dataSets={chartData} 
            />
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium text-muted-foreground">
                      Source
                    </th>
                    <th className="text-right p-2 font-medium text-muted-foreground">
                      Visitors
                    </th>
                    <th className="text-right p-2 font-medium text-muted-foreground">
                      %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item) => {
                    const percentage = totalVisitors > 0 ? ((item.count / totalVisitors) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={item.name} className="border-b hover:bg-muted/50">
                        <td className="p-2 font-medium">{item.name}</td>
                        <td className="p-2 text-right font-semibold">{item.count.toLocaleString()}</td>
                        <td className="p-2 text-right text-muted-foreground">{percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <NoData message="No data available" />
        )}
      </CardContent>
    </CardV2>
  );
};

export default EcommerceStatisticsPage;

